import React, { useEffect, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import WarningIcon from '@material-ui/icons/Warning';
import RefreshIcon from '@material-ui/icons/Refresh';
import ErrorIcon from '@material-ui/icons/Error';

const useStyles = makeStyles(theme => ({
  statusCard: {
    padding: theme.spacing(3),
    textAlign: 'center',
    backgroundColor: '#2d3748',
    borderRadius: '8px',
    border: '2px solid #4a5568',
    boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
    transition: 'border-color 0.3s ease',
    '&:hover': {
      borderColor: '#ed8936',
    },
  },
  statusIcon: {
    fontSize: '3rem',
    marginBottom: theme.spacing(1),
  },
  success: {
    color: theme.palette.success.main,
  },
  failed: {
    color: theme.palette.error.main,
  },
  warning: {
    color: theme.palette.warning.main,
  },
  contractRow: {
    padding: theme.spacing(2),
    borderBottom: '1px solid #4a5568',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#2d3748',
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #4a5568',
    transition: 'border-color 0.3s ease',
    '&:hover': {
      backgroundColor: '#374151',
      borderColor: '#ed8936',
    },
  },
  contractInfo: {
    flex: 1,
  },
  versionBadge: {
    fontFamily: 'monospace',
    fontSize: '0.85em',
    marginLeft: theme.spacing(1),
  },
  whiteText: {
    color: '#ffffff !important',
  },
  lightGrayText: {
    color: '#e0e0e0 !important',
  },
  grayText: {
    color: '#999 !important',
  },
}));

interface PactContract {
  consumer: string;
  provider: string;
  consumerVersion: string;
  providerVersion: string;
  success: boolean;
  verifiedAt: string;
  branch?: string;
  environment?: string;
}

interface PactflowData {
  applicationName: string;
  consumers: string[];
  providers: string[];
  contracts: PactContract[];
  totalContracts: number;
  successfulContracts: number;
  failedContracts: number;
}

interface PactflowCardProps {
  entity?: Entity;
}

export const PactflowCard = ({ entity }: PactflowCardProps) => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const [data, setData] = useState<PactflowData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<{
    type: 'consumer' | 'provider' | null;
    name: string | null;
  }>({ type: null, name: null });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const pactflowApp = entity?.metadata.annotations?.['pactflow.io/application'];

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    if (!pactflowApp) {
      setLoading(false);
      return;
    }

    const fetchPactflowData = async () => {
      setLoading(true);
      setError(null);
      try {
        const backendUrl = config.getString('backend.baseUrl');
        console.log('[Pactflow] Fetching data for:', pactflowApp);
        console.log('[Pactflow] Backend URL:', backendUrl);

        const providersSet = new Set<string>();
        const consumersSet = new Set<string>();

        // Optimized: Check if this app is a provider (has consumers)
        const providerPactsUrl = `${backendUrl}/api/proxy/pactflow/pacts/provider/${encodeURIComponent(
          pactflowApp,
        )}/latest`;
        console.log('[Pactflow] Checking if provider:', providerPactsUrl);

        try {
          const providerResponse = await fetch(providerPactsUrl);
          if (providerResponse.ok) {
            const providerData = await providerResponse.json();
            // Parse _links.pb:pacts to get consumer names
            if (providerData._links?.['pb:pacts']) {
              for (const pactLink of providerData._links['pb:pacts']) {
                // Extract consumer name from href: /pacts/provider/X/consumer/Y/latest
                const match = pactLink.href?.match(/\/consumer\/([^\/]+)\//);
                if (match && match[1]) {
                  consumersSet.add(match[1]);
                  console.log('[Pactflow] Found consumer:', match[1]);
                }
              }
            }
          }
        } catch (err) {
          console.log('[Pactflow] Not a provider or error:', err);
        }

        // Optimized: Check if this app is a consumer (has providers) using specific endpoint
        const consumerPactsUrl = `${backendUrl}/api/proxy/pactflow/pacts/consumer/${encodeURIComponent(
          pactflowApp,
        )}/latest`;
        console.log('[Pactflow] Checking if consumer:', consumerPactsUrl);

        try {
          const consumerResponse = await fetch(consumerPactsUrl);
          if (consumerResponse.ok) {
            const consumerData = await consumerResponse.json();
            // Parse _links.pb:pacts to get provider names
            if (consumerData._links?.['pb:pacts']) {
              for (const pactLink of consumerData._links['pb:pacts']) {
                // Extract provider name from href: /pacts/provider/X/consumer/Y/latest
                const match = pactLink.href?.match(/\/provider\/([^\/]+)\//);
                if (match && match[1]) {
                  providersSet.add(match[1]);
                  console.log('[Pactflow] Found provider:', match[1]);
                }
              }
            }
          }
        } catch (err) {
          console.log('[Pactflow] Not a consumer or error:', err);
        }

        const consumers: string[] = Array.from(consumersSet);
        const providers: string[] = Array.from(providersSet);

        console.log('[Pactflow] Found consumers:', consumers);
        console.log('[Pactflow] Found providers:', providers);

        // Fetch matrix/pacts for all relationships
        const contracts: PactContract[] = [];
        let successful = 0;
        let failed = 0;

        // Fetch contracts where this app is a consumer
        for (const provider of providers) {
          try {
            const matrixResponse = await fetch(
              `${backendUrl}/api/proxy/pactflow/matrix?q[][pacticipant]=${encodeURIComponent(
                pactflowApp,
              )}&q[][pacticipant]=${encodeURIComponent(
                provider,
              )}&latestby=cvpv`,
            );

            if (matrixResponse.ok) {
              const matrixData = await matrixResponse.json();

              if (matrixData.matrix) {
                for (const row of matrixData.matrix) {
                  const success = row.verificationResult?.success ?? false;
                  contracts.push({
                    consumer: pactflowApp,
                    provider: provider,
                    consumerVersion: row.consumer?.version?.number || 'N/A',
                    providerVersion: row.provider?.version?.number || 'N/A',
                    success: success,
                    verifiedAt:
                      row.verificationResult?.verifiedAt ||
                      new Date().toISOString(),
                    branch: row.consumer?.version?.branch,
                    environment: row.consumer?.version?.environment,
                  });

                  if (success) successful++;
                  else failed++;
                }
              }
            }
          } catch (err) {
            console.error(`Error fetching matrix for ${provider}:`, err);
          }
        }

        // Fetch contracts where this app is a provider
        for (const consumer of consumers) {
          try {
            const matrixResponse = await fetch(
              `${backendUrl}/api/proxy/pactflow/matrix?q[][pacticipant]=${encodeURIComponent(
                consumer,
              )}&q[][pacticipant]=${encodeURIComponent(
                pactflowApp,
              )}&latestby=cvpv`,
            );

            if (matrixResponse.ok) {
              const matrixData = await matrixResponse.json();

              if (matrixData.matrix) {
                for (const row of matrixData.matrix) {
                  const success = row.verificationResult?.success ?? false;
                  contracts.push({
                    consumer: consumer,
                    provider: pactflowApp,
                    consumerVersion: row.consumer?.version?.number || 'N/A',
                    providerVersion: row.provider?.version?.number || 'N/A',
                    success: success,
                    verifiedAt:
                      row.verificationResult?.verifiedAt ||
                      new Date().toISOString(),
                    branch: row.consumer?.version?.branch,
                    environment: row.consumer?.version?.environment,
                  });

                  if (success) successful++;
                  else failed++;
                }
              }
            }
          } catch (err) {
            console.error(`Error fetching matrix for ${consumer}:`, err);
          }
        }

        console.log('[Pactflow] Contracts:', contracts);

        setData({
          applicationName: pactflowApp,
          consumers: consumers,
          providers: providers,
          contracts: contracts,
          totalContracts: contracts.length,
          successfulContracts: successful,
          failedContracts: failed,
        });
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchPactflowData();
  }, [pactflowApp, config, refreshTrigger]);

  if (!pactflowApp) {
    return (
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Contract Tests
        </Typography>
        <Alert severity="info">
          No Pactflow application found. Add the{' '}
          <code>pactflow.io/application</code> annotation to your component.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Contract Tests
        </Typography>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Contract Tests
        </Typography>
        <Alert severity="error">
          Error loading Pactflow data: {error.message}
        </Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box p={3}>
        <Typography variant="h6" gutterBottom>
          Contract Tests
        </Typography>
        <Alert severity="warning">No contract test data available</Alert>
      </Box>
    );
  }

  const getStatusIcon = () => {
    if (data.failedContracts === 0 && data.successfulContracts > 0) {
      return (
        <CheckCircleIcon
          className={`${classes.statusIcon} ${classes.success}`}
        />
      );
    } else if (data.failedContracts > 0) {
      return (
        <ErrorIcon className={`${classes.statusIcon} ${classes.failed}`} />
      );
    } else {
      return (
        <WarningIcon className={`${classes.statusIcon} ${classes.warning}`} />
      );
    }
  };

  const pactflowUrl = config.getOptionalString('pactflow.baseUrl');
  const applicationUrl = pactflowUrl
    ? `${pactflowUrl}/pacticipants/${encodeURIComponent(pactflowApp)}`
    : undefined;

  return (
    <Box
      p={3}
      style={{
        backgroundColor: '#1a202c',
        borderRadius: '8px',
        minHeight: '400px',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography
            variant="h5"
            style={{ color: '#ffffff', fontWeight: 600 }}
          >
            Contract Tests
          </Typography>
          <Typography variant="body2" style={{ color: '#a0aec0' }}>
            Application: {data.applicationName}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          {applicationUrl && (
            <Typography
              variant="body2"
              component="a"
              href={applicationUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                marginRight: '16px',
                textDecoration: 'none',
                color: '#1976d2',
              }}
            >
              View in Pactflow
            </Typography>
          )}
          <Tooltip title="Refresh data">
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              size="small"
              style={{
                color: '#1976d2',
                border: '1px solid #1976d2',
                padding: '6px',
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Box className={classes.statusCard}>
            <Typography variant="h3" style={{ color: '#ffffff' }}>
              {data.totalContracts}
            </Typography>
            <Typography
              variant="body2"
              style={{ color: '#999', marginBottom: '8px' }}
            >
              Total Contracts
            </Typography>
            {getStatusIcon()}
            <Typography
              variant="body2"
              style={{ color: '#ffffff', marginTop: '4px' }}
            >
              {data.failedContracts === 0 ? 'All Passing' : 'Some Failed'}
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Box className={classes.statusCard}>
            <Typography variant="h3" className={classes.success}>
              {data.successfulContracts}
            </Typography>
            <Typography variant="body2" style={{ color: '#999' }}>
              Successful
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Box className={classes.statusCard}>
            <Typography variant="h3" className={classes.failed}>
              {data.failedContracts}
            </Typography>
            <Typography variant="body2" style={{ color: '#999' }}>
              Failed
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box className={classes.statusCard} style={{ minHeight: '120px' }}>
            <Typography
              variant="h6"
              style={{ color: '#ffffff', marginBottom: '8px' }}
            >
              Consumers
            </Typography>
            <Typography
              variant="h4"
              style={{ color: '#ffffff', marginBottom: '12px' }}
            >
              {data.consumers.length}
            </Typography>
            {data.consumers.length > 0 && (
              <Box display="flex" flexWrap="wrap" style={{ gap: '4px' }}>
                {data.consumers.map((consumer, idx) => (
                  <Chip
                    key={idx}
                    label={consumer}
                    size="small"
                    style={{ cursor: 'pointer' }}
                    color={
                      selectedFilter.type === 'consumer' &&
                      selectedFilter.name === consumer
                        ? 'secondary'
                        : 'default'
                    }
                    onClick={() =>
                      setSelectedFilter({ type: 'consumer', name: consumer })
                    }
                  />
                ))}
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box className={classes.statusCard} style={{ minHeight: '120px' }}>
            <Typography
              variant="h6"
              style={{ color: '#ffffff', marginBottom: '8px' }}
            >
              Providers
            </Typography>
            <Typography
              variant="h4"
              style={{ color: '#ffffff', marginBottom: '12px' }}
            >
              {data.providers.length}
            </Typography>
            {data.providers.length > 0 && (
              <Box display="flex" flexWrap="wrap" style={{ gap: '4px' }}>
                {data.providers.map((provider, idx) => (
                  <Chip
                    key={idx}
                    label={provider}
                    size="small"
                    color={
                      selectedFilter.type === 'provider' &&
                      selectedFilter.name === provider
                        ? 'secondary'
                        : 'primary'
                    }
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      setSelectedFilter({ type: 'provider', name: provider })
                    }
                  />
                ))}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {data.contracts.length > 0 && (
        <Box mt={4}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography
              variant="h5"
              style={{ color: '#ffffff', fontWeight: 600 }}
            >
              {selectedFilter.name
                ? `Contracts with ${selectedFilter.name}`
                : 'All Contracts'}
            </Typography>
            {selectedFilter.name && (
              <Chip
                label="Show All"
                size="small"
                onClick={() => setSelectedFilter({ type: null, name: null })}
                style={{ cursor: 'pointer' }}
              />
            )}
          </Box>
          <Box>
            {data.contracts
              .filter(contract => {
                if (!selectedFilter.name) return true;
                if (selectedFilter.type === 'consumer') {
                  return contract.consumer === selectedFilter.name;
                }
                if (selectedFilter.type === 'provider') {
                  return contract.provider === selectedFilter.name;
                }
                return true;
              })
              .map((contract, index) => (
                <Box key={index} className={classes.contractRow}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Consumer Column */}
                    <Grid item xs={12} sm={3}>
                      <Box>
                        <Typography
                          variant="body2"
                          className={classes.whiteText}
                          style={{ fontWeight: 600, marginBottom: '4px' }}
                        >
                          {contract.consumer}
                        </Typography>
                        <Chip
                          label={contract.consumerVersion}
                          size="small"
                          variant="outlined"
                          className={classes.lightGrayText}
                          style={{
                            fontSize: '0.7rem',
                            height: '20px',
                            fontFamily: 'monospace',
                            borderColor: '#666',
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Arrow */}
                    <Grid item xs={12} sm={1} style={{ textAlign: 'center' }}>
                      <Typography variant="body1" className={classes.grayText}>
                        →
                      </Typography>
                    </Grid>

                    {/* Provider Column */}
                    <Grid item xs={12} sm={3}>
                      <Box>
                        <Typography
                          variant="body2"
                          className={classes.whiteText}
                          style={{ fontWeight: 600, marginBottom: '4px' }}
                        >
                          {contract.provider}
                        </Typography>
                        <Chip
                          label={contract.providerVersion}
                          size="small"
                          variant="outlined"
                          className={classes.lightGrayText}
                          style={{
                            fontSize: '0.7rem',
                            height: '20px',
                            fontFamily: 'monospace',
                            borderColor: '#666',
                          }}
                        />
                      </Box>
                    </Grid>

                    {/* Metadata Column */}
                    <Grid item xs={12} sm={3}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        style={{ gap: '4px' }}
                      >
                        {contract.branch && (
                          <Chip
                            label={contract.branch}
                            size="small"
                            color="primary"
                            style={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        )}
                        <Typography
                          variant="caption"
                          className={classes.grayText}
                        >
                          {new Date(contract.verifiedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Status Column */}
                    <Grid item xs={12} sm={2} style={{ textAlign: 'right' }}>
                      {contract.success ? (
                        <Chip
                          icon={
                            <CheckCircleIcon style={{ fontSize: '16px' }} />
                          }
                          label="Passed"
                          size="small"
                          style={{
                            backgroundColor: '#4caf50',
                            color: 'white',
                            fontWeight: 600,
                          }}
                        />
                      ) : (
                        <Chip
                          icon={<ErrorIcon style={{ fontSize: '16px' }} />}
                          label="Failed"
                          size="small"
                          style={{
                            backgroundColor: '#f44336',
                            color: 'white',
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
